import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api, { subscriptionsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Check, Loader2, Store, CreditCard, Shield, Zap, Users, Package, BarChart3, Headphones, Sparkles, Clock, ArrowRight, UtensilsCrossed, Coffee, ShoppingBag, Wine, ShoppingCart, Scissors, Fuel, HelpCircle } from 'lucide-react';

const BUSINESS_TYPE_OPTIONS = [
    { key: "restaurant", label: "Restaurant", desc: "Restaurants, food trucks, diners", icon: UtensilsCrossed, color: "text-orange-400", bg: "bg-orange-500/10 border-orange-500/30" },
    { key: "cafe", label: "Coffee Shop / Café", desc: "Coffee, juice bars, smoothie shops", icon: Coffee, color: "text-amber-400", bg: "bg-amber-500/10 border-amber-500/30" },
    { key: "retail", label: "Retail Store", desc: "Clothing, electronics, gifts", icon: ShoppingBag, color: "text-blue-400", bg: "bg-blue-500/10 border-blue-500/30" },
    { key: "liquor", label: "Liquor Store", desc: "Liquor, wine, beer shops", icon: Wine, color: "text-purple-400", bg: "bg-purple-500/10 border-purple-500/30" },
    { key: "grocery", label: "Grocery Store", desc: "Grocery, supermarket, specialty food", icon: ShoppingCart, color: "text-green-400", bg: "bg-green-500/10 border-green-500/30" },
    { key: "salon", label: "Salon / Spa", desc: "Hair, nails, spa, barbershop", icon: Scissors, color: "text-pink-400", bg: "bg-pink-500/10 border-pink-500/30" },
    { key: "convenience", label: "Convenience Store", desc: "Gas stations, mini-marts", icon: Fuel, color: "text-yellow-400", bg: "bg-yellow-500/10 border-yellow-500/30" },
    { key: "other", label: "Other / Custom", desc: "Any other business type", icon: HelpCircle, color: "text-slate-400", bg: "bg-slate-500/10 border-slate-500/30" }
];

// Logo image URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_8df509cf-3702-445e-9c27-084cbe05056b/artifacts/o41nbhhz_default.png";

const PLANS = {
    starter: {
        name: "Starter",
        price_monthly: 29,
        price_yearly: 290,
        features: ["1 Store", "2 Users", "Basic POS", "Email Support"],
        icon: Store,
    },
    professional: {
        name: "Professional",
        price_monthly: 79,
        price_yearly: 790,
        features: ["3 Stores", "10 Users", "Full POS", "Inventory Management", "Reports", "Priority Support"],
        icon: Zap,
        popular: true
    },
    enterprise: {
        name: "Enterprise",
        price_monthly: 199,
        price_yearly: 1990,
        features: ["Unlimited Stores", "Unlimited Users", "Full POS", "Advanced Analytics", "API Access", "24/7 Support", "Custom Integrations"],
        icon: Shield,
    }
};

export default function Pricing() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { user, isAuthenticated, refreshUser } = useAuth();
    const [yearly, setYearly] = useState(true);
    const [loading, setLoading] = useState(false);
    const [trialLoading, setTrialLoading] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [storeDialogOpen, setStoreDialogOpen] = useState(false);
    const [trialDialogOpen, setTrialDialogOpen] = useState(false);
    const [dialogStep, setDialogStep] = useState(1);
    const [storeForm, setStoreForm] = useState({ name: '', address: '', phone: '', business_type: '' });
    const [currentStore, setCurrentStore] = useState(null);
    const [subscriptionStatus, setSubscriptionStatus] = useState(null);

    useEffect(() => {
        if (isAuthenticated) {
            checkSubscriptionStatus();
        }
    }, [isAuthenticated]);

    useEffect(() => {
        const sessionId = searchParams.get('session_id');
        if (sessionId) {
            verifyPayment(sessionId);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    const checkSubscriptionStatus = async () => {
        try {
            const response = await subscriptionsAPI.getStatus();
            setSubscriptionStatus(response.data);
            if (response.data.store) {
                setCurrentStore(response.data.store);
            }
        } catch (error) {
            console.log('No subscription yet');
        }
    };

    const verifyPayment = async (sessionId) => {
        try {
            const response = await subscriptionsAPI.verify(sessionId);
            if (response.data.status === 'success') {
                toast.success('Subscription activated! Redirecting to your POS dashboard...');
                await refreshUser();
                setTimeout(() => {
                    navigate('/dashboard');
                }, 2000);
            } else {
                toast.info('Payment is being processed...');
            }
        } catch (error) {
            toast.error('Failed to verify payment');
        }
    };

    const handleSelectPlan = (planKey) => {
        if (!isAuthenticated) {
            toast.info('Please log in or register to subscribe');
            navigate('/login');
            return;
        }
        setSelectedPlan(planKey);
        setDialogStep(1);
        setStoreDialogOpen(true);
    };

    const handleStartTrial = () => {
        if (!isAuthenticated) {
            toast.info('Please create an account to start your free trial');
            navigate('/login');
            return;
        }
        setDialogStep(1);
        setTrialDialogOpen(true);
    };

    const handleCreateTrialStore = async () => {
        if (!storeForm.name) { toast.error('Store name is required'); return; }
        if (!storeForm.business_type) { toast.error('Please select a business type'); return; }
        setTrialLoading(true);
        try {
            await subscriptionsAPI.startTrial({
                store_name: storeForm.name,
                business_type: storeForm.business_type,
            });
            toast.success('Your 14-day free trial has started!');
            await refreshUser();
            setTrialDialogOpen(false);
            navigate('/dashboard');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to start trial');
        } finally {
            setTrialLoading(false);
        }
    };

    const handleCreateStore = async () => {
        if (!storeForm.name) { toast.error('Store name is required'); return; }
        if (!storeForm.business_type) { toast.error('Please select a business type'); return; }
        setStoreDialogOpen(false);
        proceedToCheckout(selectedPlan);
    };

    const proceedToCheckout = async (planKey) => {
        setLoading(true);
        try {
            const response = await subscriptionsAPI.createCheckout({
                plan: planKey,
                billing_cycle: yearly ? 'yearly' : 'monthly',
                origin_url: window.location.origin,
                store_name: storeForm.name || `${user?.name || 'My'}'s Store`,
                business_type: storeForm.business_type || 'restaurant',
            });
            window.location.href = response.data.url;
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create checkout');
            setLoading(false);
        }
    };

    // If user has active subscription or trial, show status
    if (subscriptionStatus?.status === 'active' || subscriptionStatus?.status === 'trial') {
        const isTrial = subscriptionStatus.status === 'trial';
        return (
            <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
                <Card className="max-w-md w-full bg-slate-900 border-slate-800">
                    <CardHeader className="text-center">
                        <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${isTrial ? 'bg-cyan-500/20' : 'bg-emerald-500/20'}`}>
                            {isTrial ? <Clock className="h-8 w-8 text-cyan-400" /> : <Check className="h-8 w-8 text-emerald-400" />}
                        </div>
                        <CardTitle className="text-white">{isTrial ? 'Free Trial Active' : 'Subscription Active'}</CardTitle>
                        <CardDescription className="text-slate-400">
                            {isTrial 
                                ? `Trial ends ${new Date(subscriptionStatus.trial_end).toLocaleDateString()}`
                                : `You're on the ${PLANS[subscriptionStatus.plan]?.name || subscriptionStatus.plan} plan`
                            }
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                            <p className="text-sm text-slate-400">Store</p>
                            <p className="font-semibold text-white">{subscriptionStatus.store?.name}</p>
                        </div>
                        {isTrial && (
                            <div className="p-4 bg-cyan-500/10 rounded-lg border border-cyan-500/30">
                                <p className="text-sm text-cyan-300">
                                    Enjoying Mezgeb POS? Upgrade to a paid plan anytime to keep your data after the trial ends.
                                </p>
                            </div>
                        )}
                    </CardContent>
                    <CardFooter className="flex gap-2">
                        <Button className="flex-1 bg-cyan-500 hover:bg-cyan-600 text-slate-950 font-semibold" onClick={() => navigate('/dashboard')}>
                            Go to Dashboard
                        </Button>
                        {isTrial && (
                            <Button variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800" onClick={() => setSubscriptionStatus(null)}>
                                View Plans
                            </Button>
                        )}
                    </CardFooter>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-950" data-testid="pricing-page">
            {/* Header */}
            <header className="border-b border-slate-800">
                <div className="container mx-auto px-4 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
                        <img src={LOGO_URL} alt="Mezgeb POS" className="h-8 w-auto" />
                    </div>
                    {!isAuthenticated ? (
                        <Button variant="outline" onClick={() => navigate('/login')} className="border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white">
                            Sign In
                        </Button>
                    ) : (
                        <Button variant="outline" onClick={() => navigate('/dashboard')} className="border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white">
                            Dashboard
                        </Button>
                    )}
                </div>
            </header>

            {/* Hero */}
            <section className="py-16 px-4 text-center">
                <Badge className="mb-4 bg-cyan-500/20 text-cyan-400 border-cyan-500/30">PRICING</Badge>
                <h1 className="text-4xl md:text-5xl font-bold mb-4 text-white">
                    Simple, Transparent Pricing
                </h1>
                <p className="text-xl text-slate-400 mb-8 max-w-2xl mx-auto">
                    Choose the perfect plan for your retail business. No hidden fees.
                </p>

                {/* Free Trial Banner */}
                <Card className="max-w-2xl mx-auto mb-12 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border-cyan-500/30">
                    <CardContent className="p-6 flex flex-col md:flex-row items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-cyan-500/20 rounded-full flex items-center justify-center">
                                <Sparkles className="h-6 w-6 text-cyan-400" />
                            </div>
                            <div className="text-left">
                                <h3 className="font-semibold text-white">Start Your 14-Day Free Trial</h3>
                                <p className="text-sm text-slate-400">Full access to all features. No credit card required.</p>
                            </div>
                        </div>
                        <Button 
                            onClick={handleStartTrial}
                            className="bg-cyan-500 hover:bg-cyan-600 text-slate-950 font-semibold whitespace-nowrap"
                            data-testid="start-trial-btn"
                        >
                            Start Free Trial
                            <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                    </CardContent>
                </Card>

                {/* Billing Toggle */}
                <div className="flex items-center justify-center gap-4 mb-12">
                    <span className={!yearly ? "font-semibold text-white" : "text-slate-500"}>Monthly</span>
                    <Switch
                        checked={yearly}
                        onCheckedChange={setYearly}
                        data-testid="billing-toggle"
                    />
                    <span className={yearly ? "font-semibold text-white" : "text-slate-500"}>
                        Yearly
                        <Badge className="ml-2 bg-emerald-500/20 text-emerald-400 border-emerald-500/30">Save 17%</Badge>
                    </span>
                </div>
            </section>

            {/* Pricing Cards */}
            <section className="pb-16 px-4">
                <div className="container mx-auto grid md:grid-cols-3 gap-6 max-w-6xl">
                    {Object.entries(PLANS).map(([key, plan]) => (
                        <Card 
                            key={key}
                            className={`relative bg-slate-900 border-slate-800 ${plan.popular ? 'border-cyan-500 shadow-lg shadow-cyan-500/20 scale-105' : 'hover:border-slate-700'} transition-all`}
                            data-testid={`plan-${key}`}
                        >
                            {plan.popular && (
                                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                                    <Badge className="bg-cyan-500 text-slate-950 font-semibold">Most Popular</Badge>
                                </div>
                            )}
                            <CardHeader>
                                <div className="w-12 h-12 rounded-lg bg-slate-800 border border-slate-700 flex items-center justify-center mb-4">
                                    <plan.icon className="h-6 w-6 text-cyan-400" />
                                </div>
                                <CardTitle className="text-white">{plan.name}</CardTitle>
                                <div className="mt-4">
                                    <span className="text-4xl font-bold text-white">
                                        ${yearly ? plan.price_yearly : plan.price_monthly}
                                    </span>
                                    <span className="text-slate-500">
                                        /{yearly ? 'year' : 'month'}
                                    </span>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <ul className="space-y-3">
                                    {plan.features.map((feature, idx) => (
                                        <li key={idx} className="flex items-center gap-2">
                                            <Check className="h-4 w-4 text-cyan-400 shrink-0" />
                                            <span className="text-sm text-slate-300">{feature}</span>
                                        </li>
                                    ))}
                                </ul>
                            </CardContent>
                            <CardFooter>
                                <Button 
                                    className={`w-full ${plan.popular ? 'bg-cyan-500 hover:bg-cyan-600 text-slate-950 font-semibold' : 'bg-slate-800 hover:bg-slate-700 text-white border border-slate-700'}`}
                                    onClick={() => handleSelectPlan(key)}
                                    disabled={loading}
                                    data-testid={`select-${key}`}
                                >
                                    {loading && selectedPlan === key ? (
                                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                    ) : null}
                                    Get Started
                                </Button>
                            </CardFooter>
                        </Card>
                    ))}
                </div>
            </section>

            {/* Features Section */}
            <section className="py-16 px-4 bg-slate-900/50">
                <div className="container mx-auto max-w-4xl text-center">
                    <h2 className="text-3xl font-bold mb-8 text-white">Everything You Need</h2>
                    <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-6">
                        {[
                            { icon: Package, title: "Inventory", desc: "Track stock in real-time" },
                            { icon: Users, title: "Multi-User", desc: "Role-based access" },
                            { icon: BarChart3, title: "Reports", desc: "Sales analytics" },
                            { icon: Headphones, title: "Support", desc: "We're here to help" }
                        ].map((item, idx) => (
                            <div key={idx} className="p-4">
                                <item.icon className="h-8 w-8 mx-auto mb-3 text-cyan-400" />
                                <h3 className="font-semibold text-white">{item.title}</h3>
                                <p className="text-sm text-slate-400">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section className="py-16 px-4 bg-slate-950">
                <div className="container mx-auto max-w-2xl">
                    <h2 className="text-3xl font-bold mb-8 text-center text-white">FAQ</h2>
                    <div className="space-y-4">
                        {[
                            { q: "Can I cancel anytime?", a: "Yes, you can cancel your subscription at any time. No questions asked." },
                            { q: "Is there a free trial?", a: "Yes! Start a 14-day free trial with full access to all features. No credit card required." },
                            { q: "Can I upgrade later?", a: "Absolutely! Upgrade your plan anytime and only pay the difference." },
                            { q: "What payment methods do you accept?", a: "We accept all major credit cards via Stripe." }
                        ].map((item, idx) => (
                            <Card key={idx} className="bg-slate-900 border-slate-800">
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-lg text-white">{item.q}</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <p className="text-slate-400">{item.a}</p>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* Trial Store Creation Dialog */}
            <Dialog open={trialDialogOpen} onOpenChange={(open) => { setTrialDialogOpen(open); if (!open) setDialogStep(1); }}>
                <DialogContent data-testid="trial-dialog" className="bg-slate-900 border-slate-800 max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="text-white">
                            {dialogStep === 1 ? 'What type of business do you have?' : 'Start Your Free Trial'}
                        </DialogTitle>
                        <DialogDescription className="text-slate-400">
                            {dialogStep === 1
                                ? 'We\'ll set up your categories and tools to match your business.'
                                : 'Set up your store to begin your 14-day free trial. No credit card required.'
                            }
                        </DialogDescription>
                    </DialogHeader>

                    {dialogStep === 1 && (
                        <div className="grid grid-cols-2 gap-3 py-2">
                            {BUSINESS_TYPE_OPTIONS.map((bt) => (
                                <button
                                    key={bt.key}
                                    onClick={() => setStoreForm({ ...storeForm, business_type: bt.key })}
                                    className={`flex items-start gap-3 p-3 rounded-lg border text-left transition-all ${storeForm.business_type === bt.key ? bt.bg + ' ring-2 ring-cyan-500' : 'border-slate-700 hover:border-slate-600 bg-slate-800/50'}`}
                                >
                                    <bt.icon className={`h-5 w-5 mt-0.5 shrink-0 ${storeForm.business_type === bt.key ? bt.color : 'text-slate-400'}`} />
                                    <div>
                                        <p className="text-sm font-medium text-white">{bt.label}</p>
                                        <p className="text-xs text-slate-400">{bt.desc}</p>
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}

                    {dialogStep === 2 && (
                        <div className="space-y-4">
                            <div className="p-4 bg-cyan-500/10 rounded-lg border border-cyan-500/30">
                                <div className="flex items-center gap-2 text-cyan-400 font-medium mb-1">
                                    <Sparkles className="h-4 w-4" />
                                    What's included in your trial:
                                </div>
                                <ul className="text-sm text-slate-300 space-y-1 ml-6">
                                    <li>• Full access to all Professional features</li>
                                    <li>• Pre-configured categories for your business type</li>
                                    <li>• Unlimited products and sales</li>
                                </ul>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="trial-store-name" className="text-slate-300">Business Name *</Label>
                                <Input
                                    id="trial-store-name"
                                    value={storeForm.name}
                                    onChange={(e) => setStoreForm({ ...storeForm, name: e.target.value })}
                                    placeholder="My Restaurant"
                                    data-testid="trial-store-name-input"
                                    className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="trial-store-address" className="text-slate-300">Address (optional)</Label>
                                <Input
                                    id="trial-store-address"
                                    value={storeForm.address}
                                    onChange={(e) => setStoreForm({ ...storeForm, address: e.target.value })}
                                    placeholder="123 Main St, City"
                                    className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                                />
                            </div>
                        </div>
                    )}

                    <DialogFooter>
                        {dialogStep === 1 ? (
                            <>
                                <Button variant="outline" onClick={() => setTrialDialogOpen(false)} className="border-slate-700 text-slate-300 hover:bg-slate-800">Cancel</Button>
                                <Button
                                    onClick={() => setDialogStep(2)}
                                    disabled={!storeForm.business_type}
                                    className="bg-cyan-500 hover:bg-cyan-600 text-slate-950 font-semibold"
                                >
                                    Next <ArrowRight className="ml-2 h-4 w-4" />
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button variant="outline" onClick={() => setDialogStep(1)} className="border-slate-700 text-slate-300 hover:bg-slate-800">Back</Button>
                                <Button onClick={handleCreateTrialStore} disabled={trialLoading} className="bg-cyan-500 hover:bg-cyan-600 text-slate-950 font-semibold">
                                    {trialLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                                    Start Free Trial
                                </Button>
                            </>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Paid Store Creation Dialog */}
            <Dialog open={storeDialogOpen} onOpenChange={(open) => { setStoreDialogOpen(open); if (!open) setDialogStep(1); }}>
                <DialogContent data-testid="store-dialog" className="bg-slate-900 border-slate-800 max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="text-white">
                            {dialogStep === 1 ? 'What type of business do you have?' : 'Create Your Store'}
                        </DialogTitle>
                        <DialogDescription className="text-slate-400">
                            {dialogStep === 1
                                ? 'We\'ll pre-configure categories and settings to match your business.'
                                : 'Set up your store before subscribing.'
                            }
                        </DialogDescription>
                    </DialogHeader>

                    {dialogStep === 1 && (
                        <div className="grid grid-cols-2 gap-3 py-2">
                            {BUSINESS_TYPE_OPTIONS.map((bt) => (
                                <button
                                    key={bt.key}
                                    onClick={() => setStoreForm({ ...storeForm, business_type: bt.key })}
                                    className={`flex items-start gap-3 p-3 rounded-lg border text-left transition-all ${storeForm.business_type === bt.key ? bt.bg + ' ring-2 ring-cyan-500' : 'border-slate-700 hover:border-slate-600 bg-slate-800/50'}`}
                                >
                                    <bt.icon className={`h-5 w-5 mt-0.5 shrink-0 ${storeForm.business_type === bt.key ? bt.color : 'text-slate-400'}`} />
                                    <div>
                                        <p className="text-sm font-medium text-white">{bt.label}</p>
                                        <p className="text-xs text-slate-400">{bt.desc}</p>
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}

                    {dialogStep === 2 && (
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="store-name" className="text-slate-300">Business Name *</Label>
                                <Input
                                    id="store-name"
                                    value={storeForm.name}
                                    onChange={(e) => setStoreForm({ ...storeForm, name: e.target.value })}
                                    placeholder="My Retail Store"
                                    data-testid="store-name-input"
                                    className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="store-address" className="text-slate-300">Address</Label>
                                <Input
                                    id="store-address"
                                    value={storeForm.address}
                                    onChange={(e) => setStoreForm({ ...storeForm, address: e.target.value })}
                                    placeholder="123 Main St, City"
                                    className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="store-phone" className="text-slate-300">Phone</Label>
                                <Input
                                    id="store-phone"
                                    value={storeForm.phone}
                                    onChange={(e) => setStoreForm({ ...storeForm, phone: e.target.value })}
                                    placeholder="+1 (555) 123-4567"
                                    className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                                />
                            </div>
                        </div>
                    )}

                    <DialogFooter>
                        {dialogStep === 1 ? (
                            <>
                                <Button variant="outline" onClick={() => setStoreDialogOpen(false)} className="border-slate-700 text-slate-300 hover:bg-slate-800">Cancel</Button>
                                <Button
                                    onClick={() => setDialogStep(2)}
                                    disabled={!storeForm.business_type}
                                    className="bg-cyan-500 hover:bg-cyan-600 text-slate-950 font-semibold"
                                >
                                    Next <ArrowRight className="ml-2 h-4 w-4" />
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button variant="outline" onClick={() => setDialogStep(1)} className="border-slate-700 text-slate-300 hover:bg-slate-800">Back</Button>
                                <Button onClick={handleCreateStore} disabled={loading} className="bg-cyan-500 hover:bg-cyan-600 text-slate-950 font-semibold">
                                    {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                                    Continue to Payment
                                </Button>
                            </>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
